import { el } from './dom';

export type DropZoneProps = {
  accept?: string;
  multiple?: boolean;
  title?: string;
  meta?: string;
  onFiles?: (files: File[]) => void;
};

const DEFAULTS = {
  accept: '.pdf,.png,.jpg,.jpeg,.heic,.webp,.txt,.csv,image/*,application/pdf,text/*',
  multiple: true,
  title: 'drop a file or click to upload',
  meta: 'PDF · PNG · JPG · HEIC — up to 10 MB',
};

const defaultOnFiles = (): void => {
  /* no-op — real handler is wired up in main.ts */
};

export const DropZone = (props: DropZoneProps = {}): HTMLElement => {
  const accept = props.accept ?? DEFAULTS.accept;
  const multiple = props.multiple ?? DEFAULTS.multiple;
  const title = props.title ?? DEFAULTS.title;
  const meta = props.meta ?? DEFAULTS.meta;
  const onFiles = props.onFiles ?? defaultOnFiles;

  const root = el(`
    <div class="drop-zone" id="drop-zone" role="button" tabindex="0" aria-label="${title}">
      <span class="drop-zone-corner tl">+</span>
      <span class="drop-zone-corner tr">+</span>
      <span class="drop-zone-corner bl">+</span>
      <span class="drop-zone-corner br">+</span>
      <div class="drop-zone-title">${title}</div>
      <div class="drop-zone-meta">${meta}</div>
      <input type="file" hidden ${multiple ? 'multiple' : ''} accept="${accept}" />
    </div>
  `);

  const fileInput = root.querySelector<HTMLInputElement>('input[type="file"]');
  if (!fileInput) throw new Error('DropZone: file input missing');

  // dragenter/dragleave fire for child elements too; use a counter to track
  // whether the cursor is actually inside the zone.
  let depth = 0;
  const setDragover = (active: boolean): void => {
    root.classList.toggle('is-dragover', active);
  };

  root.addEventListener('dragenter', (e) => {
    e.preventDefault();
    depth += 1;
    setDragover(true);
  });
  root.addEventListener('dragover', (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  });
  root.addEventListener('dragleave', (e) => {
    e.preventDefault();
    depth = Math.max(0, depth - 1);
    if (depth === 0) setDragover(false);
  });
  root.addEventListener('drop', (e) => {
    e.preventDefault();
    depth = 0;
    setDragover(false);
    const files = e.dataTransfer ? Array.from(e.dataTransfer.files) : [];
    if (files.length > 0) onFiles(files);
  });

  root.addEventListener('click', () => {
    fileInput.click();
  });
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });

  fileInput.addEventListener('change', () => {
    const files = fileInput.files ? Array.from(fileInput.files) : [];
    if (files.length > 0) onFiles(files);
    fileInput.value = '';
  });

  return root;
};
